<template lang="pug">
#layout-base
  //- header
  b-navbar#header(toggleable="false" :sticky="true")
    b-navbar-brand.p-0
      img.title(src="@/assets/home-Logo-mobile.png" @click="gotoHome()")
    button.toggle-side-menu(type="button" )
      .menu-bar(v-b-toggle.sidebar)

  //- sidebar
  b-sidebar#sidebar(title="Sidebar" header-class="sidebar" body-class="sidebar" bg-variant="dark" text-variant="light" backdrop right shadow)
    //- menu title
    template(#header="{ hide }")
      .side-bar-header.d-flex.flex-row.align-items-center
        .flex-grow-1
          .user-names.d-flex.flex-column
            .user-name
              span.menu-user-name {{ user.name }}
              span.menu-user-role(v-if="user.role == 0") （KMユーザー）
              span.menu-user-role(v-if="user.role == 1") （管理者ユーザー）
              span.menu-user-role(v-if="user.role == 2") （一般ユーザー）
            .user-id
              span.menu-user-id {{ user.userId }}
        b-icon(icon="b-icon-x" @click="hide")
    //- menu contents
    .sidebar-menu
      .menu-tab
        a(@click="conformLogout")
          .d-flex.flex-row.align-items-center
            span.tab-text ログアウト

  //- main contents
  b-container#content(fluid)
    b-row
      b-col
        //- フッターメニューに指がひっかかるので、下はpaddingを取って余白を作る
        router-view.pb-4(v-if="_useFamilyService"  @refresh="refreshBadge" @updateShowFooter="updateShowFooter")
        //- 家族連絡サービスを使用していない場合はメッセージを表示する
        span(v-else) 使用できるメニューがありません

  //- footer navigation fadein/fadeout transition
  transition(v-if="_useFamilyService" name="footer-transition")
    //- footer navigation
    b-container#footer(v-show="showFooter" fluid)
      b-row
        b-col.menu-item.p-0
          router-link.p-1.d-flex.flex-column(:to="urls.Home" @click.native="refreshBadge")
            .container-fluid.mb-1
              img.footer-menu-icon(src="@/assets/footermenu-home-mobile.png")
            span.footer-menu-name ホーム
        b-col.menu-item.p-0(v-if="useFunction('sendMessagesFunction') && _showSendMessages")
          router-link.p-1.d-flex.flex-column(:to="urls.MessageRegisterInput" @click.native="refreshBadge")
            .container-fluid.mb-1
              img.footer-menu-icon(src="@/assets/footermenu-sendmessage-mobile.png")
            span.footer-menu-name 新規送信
        b-col.menu-item.p-0(v-if="showNewArrivalMessage()")
          router-link.p-1.d-flex.flex-column(:to="urls.NewArrivalMessagesList" @click.native="refreshBadge")
            .container-fluid.mb-1
              b-img.footer-menu-icon(src="@/assets/footermenu-receivemessage-mobile.png")
              span.badge(v-if="_showNewArrivalBadge" :class="_newArrivalBadgeClass")
            span.footer-menu-name 新着メッセージ

  //- logout modal component
  OkCancelModal(v-model="showLogout" title="ログアウトしますか？" @ok="onLogoutOk")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { Getter } from "vuex-class";
import { userStore } from "@/store/store";
import { MobileUrl } from "@/commons/constants/MobileUrl";
import { Url } from "@/commons/constants/Url";
import SystemError from "@/commons/models/SystemError";

import SendMessageRepliesService, { SendMessageRepliesUnreadModel } from "@/commons/services/SendMessageReplies";
import OtherUserGroupSendMessageRepliesService from "@/commons/services/OtherUserGroupSendMessageReplies";
import OpinionsService from "@/commons/services/Opinions";
import OtherUserOpinionsService from "@/commons/services/OtherUserOpinions";
import { Account } from "@/commons/services/Authorization";
import { PlanType } from "@/commons/services/Plans";
import { FacilityGetModel } from "@/commons/services/Facilities";

import OkCancelModal from "@/mobile/basics/OkCancelModal.vue";

/**
 * ### 基底レイアウトコンポーネント
 *
 * - ヘッダー、サイドバー、フッターの機能を提供する
 * - 本コンポーネントに共通する機能を何でもかんでも詰め込まないように注意すること
 * - base(基底)であって、common(共通)という責務ではないことを意識すること
 */
@Component({
  components: {
    OkCancelModal
  }
})
export default class LayoutDefault extends Vue {
  /** メッセージ送信の返信サービス */
  @Inject("SendMessageRepliesService") sendMessageRepliesService!: SendMessageRepliesService;
  /** メッセージ送信の返信サービス */
  @Inject("OtherUserGroupSendMessageRepliesService") otherUserGroupSendMessageRepliesService!: OtherUserGroupSendMessageRepliesService;
  /** 意見サービス */
  @Inject("OpinionsService") opinionsService!: OpinionsService;
  /** 意見サービス */
  @Inject("OtherUserOpinionsService") otherUserOpinionService!: OtherUserOpinionsService;

  /** ユーザー名。ユーザー情報修正画面で更新されるので、storeのgetterから値を取得する */
  @Getter("user") user!: Account;
  /** ユーザー名。ユーザー情報修正画面で更新されるので、storeのgetterから値を取得する */
  @Getter("getFacility") facility!: FacilityGetModel;

  /** 送信メッセージの返信があるか */
  showSendMessageIncoming: boolean = false;
  /** 未確認の受信メッセージ数 */
  nonFinishedOpinionCount: number | string = 0;

  /** templateで使用するので、importしたUrlクラスをdataとして保持する */
  urls: {} = MobileUrl;

  /** フッターの表示可否 */
  showFooter: boolean = true;
  /** ログアウトモーダルの表示可否 */
  showLogout: boolean = false;

  get _useFamilyService(): boolean {
    return this.facility.useFamilyService;
  }

  get _showNewArrivalBadge(): boolean {
    if (this.showSendMessageIncoming == true) {
      return true;
    }
    if (0 < Number(this.nonFinishedOpinionCount)) {
      return true;
    }
    return false;
  }

  get _showSendMessages(): boolean {
    return userStore.loginUser.permissions.sendMessagesFunction.useSendAndReply;
  }

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    // コンテンツエリアの表示対象を決める
    // 基本的には現在のRoute値へ遷移するが、本画面(PcUrl.Dashboard)の場合はホーム画面を表示する
    if (this.$route.path == MobileUrl.Dashboard) {
      this.$router.push(MobileUrl.Home);
    } else {
      this.$router.push({ name: this.$route.name, params: this.$route.params });
    }

    this.refreshBadge();

    return;
  }

  /**
   * ### バッジのリフレッシュ
   *
   * - バッジの更新でシステムエラーにはしないため、コンソールエラーだけにする
   */
  public async refreshBadge(): Promise<void> {
    Promise.all([
      // 非同期で呼び出している理由は更新が完了次第画面を更新するため
      this.updateNonFinishedOpinionCount(),
      this.updateShowSendMessageIncoming()
    ]).then((results: Array<any>) => {
      for (const result of results) {
        if (result instanceof SystemError) {
          console.error("error:", result);
        }
      }
    });

    return;
  }

  /**
   * ### メッセージ送信の返信未読があるかを取得する
   */
  public async updateShowSendMessageIncoming(): Promise<void | SystemError> {
    try {
      const showSendMessageIncoming: SendMessageRepliesUnreadModel = await this.sendMessageRepliesService.getUnreadInFacility(userStore);
      const showOtherUserGroupSendMessageIncoming: SendMessageRepliesUnreadModel = await this.otherUserGroupSendMessageRepliesService.getUnreadInFacility(userStore);

      if (showSendMessageIncoming.unread == true || showOtherUserGroupSendMessageIncoming.unread == true) {
        this.showSendMessageIncoming = true;
      } else {
        this.showSendMessageIncoming = false;
      }
    } catch (error) {
      // 配布していない問い合わせが取得できない場合は0を格納して非表示にする
      this.showSendMessageIncoming = false;
      // Promise.all()で並列に実行されるので例外を投げなおすのではなく例外を戻り値として返す
      return error as SystemError;
    }

    return;
  }

  /**
   * ### 未確認の受信メッセージ数を更新する
   */
  public async updateNonFinishedOpinionCount(): Promise<void | SystemError> {
    // 家族からの連絡機能が使用不可の場合は即returnする
    if (!userStore.facility.permittedFunctions.opinion) {
      this.nonFinishedOpinionCount = 0;
      return;
    }

    try {
      const params: any = {
        confirmed: false
      };
      const count: number = await this.opinionsService.getCount(userStore, params);
      const otheruserCount: number = await this.otherUserOpinionService.getCount(userStore, params);

      // 100件以上の場合は!を格納する
      if (100 <= count + otheruserCount) {
        this.nonFinishedOpinionCount = "!";
      } else {
        this.nonFinishedOpinionCount = count + otheruserCount;
      }
    } catch (error) {
      // 機能使用不可でステータスコード405が返却された場合はエラーとして扱わない
      const systemError: SystemError = error as SystemError;
      this.nonFinishedOpinionCount = 0;
      if (systemError.statusCode == 405) {
        return undefined;
      } else {
        // Promise.all()で並列に実行されるので例外を投げなおすのではなく例外を戻り値として返す
        return error as SystemError;
      }
    }

    return;
  }

  /**
   * ### 一般ユーザーの機能使用可否を返す
   */
  public useFunction(key: string): boolean {
    const functions: any = userStore.user.permissions.functions;
    const used: boolean = functions[key];

    return used;
  }

  /**
   * ### 新着メッセージのバッジclassを返す
   *
   * - フッターメニュー数はユーザー毎の機能使用可否で動的に切り替わる
   * - バッジは絶対位置指定なので、メニュー数に応じて動的にCSSクラスを返す必要がある
   * - メニュー数の判定条件は以下
   * - 3つ
   *   - メッセージ送信が使用可
   * - 2つ
   *   - メッセージ送信が使用不可(3つかどうかのチェックのelse)
   *   - 受信メッセージが使用可 && プランがベーシック以上
   *   - 受信メッセージが使用可 && 家族からの連絡が使用可
   * - 1つ
   *   - 3つ、2つではない
   */
  get _newArrivalBadgeClass(): string {
    // メニュー3つ
    if (this.useFunction("sendMessagesFunction") == true) {
      return "three-menu-badge";
    }
    // メニュー2つ
    else if (
      (this.useFunction("receiveMessagesFunction") == true && PlanType.BASIC <= userStore.facility.planType) ||
      (this.useFunction("receiveMessagesFunction") == true && userStore.facility.permittedFunctions.opinion == true)
    ) {
      return "two-menu-badge";
    }
    // メニュー1つの場合は新着メッセージが非表示なのでバッジ不要
    else {
      return "";
    }
  }

  /**
   * ### 新着メッセージの表示可否を返す
   *
   * - 新着メッセージには以下の情報を表示する
   *   - 送信履歴(家族)
   *   - 送信履歴(施設関係者)
   *   - 家族からの連絡
   *   - 施設関係者からの連絡
   * - 上記4つのうちいずれかが表示される場合は新着メッセージを表示する必要がある
   * - 判定条件は以下
   *   - メッセージ送信が使用可：「送信履歴(家族)」「送信履歴(施設関係者)」がある
   *   - 受信メッセージが使用可 && プランがベーシック以上：「施設関係者からの連絡」がある
   *   - 受信メッセージが使用可 && 家族からの連絡が使用可：「家族からの連絡」がある
   */
  public showNewArrivalMessage(): boolean {
    if (
      this.useFunction("sendMessagesFunction") == true ||
      (this.useFunction("receiveMessagesFunction") == true && PlanType.BASIC <= userStore.facility.planType) ||
      (this.useFunction("receiveMessagesFunction") == true && userStore.facility.permittedFunctions.opinion == true)
    ) {
      return true;
    } else {
      return false;
    }
  }

  /**
   * ### ホーム画面へ遷移する
   */
  public gotoHome(): void {
    this.$router.push(MobileUrl.Home);
    return;
  }

  /**
   * ### フッターの表示を切り替える
   */
  public updateShowFooter(showFooter: boolean): void {
    this.showFooter = showFooter;
    return;
  }

  /**
   * ### ログアウトモーダルを表示する
   */
  public conformLogout(): void {
    this.showLogout = true;
    return;
  }

  /**
   * ### ログアウト実施
   */
  public onLogoutOk(): void {
    userStore.logout();
    this.$router.push(Url.Root);
    return;
  }
}
</script>

<style lang="scss" scoped>
@import "../../styles/mobile/_mixin.scss";
@import "../../styles/mobile/_colors.scss";

#layout-base {
  #header {
    background-color: $bg-default !important;
    border-bottom: solid 2px $font-key;

    .title {
      // 画像が左にマージンがあるので相殺する
      margin-left: -8px;
      height: 26px;
    }

    .toggle-side-menu {
      background-color: $bg-btn;
      border: 1px solid $border-btn;
      border-radius: 0.25rem;
      .bars {
        color: $font-btn;
      }
    }

    .menu-bar {
      @include svg-icon("../../assets/bars.svg", $font-base, 16px, 16px);
    }
  }
  #sidebar {
    .side-bar-header {
      width: 100%;

      .user-names {
        max-width: 270px;
        .user-name,
        .user-id {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          font-size: 0.8rem;
        }
        .user-name {
          .menu-user-name {
            font-size: 1rem;
          }
          .menu-user-role {
            font-size: 0.8rem;
          }
        }
        .user-id {
          .menu-user-id {
            font-size: 0.8rem;
          }
        }
      }
    }
    a {
      color: #ffffff;
    }
    // メニューの左側のアイコン
    .sidebar-menu {
      padding: 0.5rem 1rem;
      .menu-icon {
        margin-right: 0.5rem;
      }
      .menu-tab {
        margin-bottom: 0.5rem;
      }
    }
  }
  #content {
    position: relative;
    background-color: $bg-default;
    padding: 0.5rem 1rem;
    // フッターを一番下に表示するために、コンテンツ領域の高さは計算で求める必要がある
    // ヘッダー:48px、フッター:60px
    min-height: calc(100vh - 48px - 63px);
  }
  #footer {
    position: sticky;
    bottom: 0;
    z-index: 1020;

    .row {
      background-color: $bg-footer;
      border-top: 1px solid $border-footer;
    }

    .menu-item {
      position: relative;
      font-size: 0.8rem;
      text-align: center;
      border-right: 2px solid $border-footer;

      .footer-menu-icon {
        width: 32px;
        height: 32px;
      }
      .badge {
        margin: 0 0 0 0px !important;
        display: inline-block;
        min-width: 10px;
        padding: 7px 7px;
        font-size: 0.9rem;
        font-weight: bold;
        color: #fff;
        line-height: 1;
        vertical-align: middle;
        white-space: nowrap;
        text-align: center;
        background-color: $bg-badge;
        border-radius: 10px;
      }
      .three-menu-badge {
        position: absolute;
        top: 4px;
        left: 18vw;
      }
      .two-menu-badge {
        position: absolute;
        top: 4px;
        left: 26.5vw;
      }
      .footer-menu-name {
        color: $font-footer !important;
      }
      a {
        text-decoration: none !important;
      }
    }
    .menu-item:last-child {
      border-right: 0;
    }
  }
  // フッターのフェードイン/アウト
  .footer-transition-enter-active,
  .footer-transition-leave-active {
    transition: opacity 0.5s;
  }
  .footer-transition-enter,
  .footer-transition-leave-to {
    opacity: 0;
  }
}
</style>
